import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../../stores/authStore";
import { Clock, KanbanSquare, KeyRound, ListChecks, LogOut, User, Users } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useCapacidades } from "../../features/chamados/gestao/api";
import { useIdentidade } from "../../features/conta/api";
import { TrocarSenhaDialog } from "../../features/conta/TrocarSenhaDialog";
import { paginaDeEntrada } from "../../features/equipe/sessao";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { user, logout, setUser } = useAuth();

  // Nome, cliente e perfil vêm do cadastro a cada carga: o que o login guardou
  // pode ter mudado desde então.
  const { data: identidade } = useIdentidade(Boolean(user));
  useEffect(() => {
    if (!user || !identidade) return;
    const nomeCliente = identidade.nomeCliente ?? user.nomeCliente;
    const nomeContrato = identidade.nomeContrato ?? user.nomeContrato ?? null;
    const mudou =
      identidade.nome !== user.nome ||
      identidade.email !== user.email ||
      identidade.administrador !== user.administrador ||
      nomeCliente !== user.nomeCliente ||
      nomeContrato !== (user.nomeContrato ?? null);
    if (mudou)
      setUser({
        ...user,
        nome: identidade.nome,
        email: identidade.email,
        administrador: identidade.administrador,
        nomeCliente,
        nomeContrato,
      });
  }, [identidade, user, setUser]);

  // A troca de senha é escrita: aparece onde a bandeira está ligada, como as
  // demais ações do cliente.
  const { data: capacidades } = useCapacidades();
  const [trocandoSenha, setTrocandoSenha] = useState(false);

  // Equipe T4i: atende um contrato por vez (desde 09/10/2026; antes, um cliente),
  // sem senha do portal. Vê o quadro do contrato escolhido (pedido de 05/10/2026),
  // sem mexer na prioridade, que é a ordem do cliente. Sem contrato escolhido,
  // não há o que navegar.
  const equipe = Boolean(user?.equipe);
  const comCliente = !equipe || Boolean(user?.idContrato);

  const handleLogout = () => {
    // Lido antes de limpar: a equipe volta para a própria entrada.
    const entrada = paginaDeEntrada();
    logout();
    window.location.href = entrada;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Faixa da equipe T4i em todas as telas: quem responde sabe sempre em
          qual cliente e contrato está, e o cliente nunca vê esta faixa. */}
      {equipe && (
        <div className="border-b border-violet-200 bg-violet-50 text-violet-900">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 py-1.5 text-sm sm:px-6 lg:px-8">
            <span className="flex min-w-0 items-center gap-2">
              <Users className="h-4 w-4 shrink-0" />
              <span className="shrink-0 font-semibold">Equipe T4i</span>
              <span className="truncate">
                {comCliente ? (
                  <>
                    · atendendo <strong className="font-semibold">{user?.nomeCliente}</strong>
                    {user?.nomeContrato && <> · {user.nomeContrato}</>}
                  </>
                ) : (
                  "· nenhum contrato escolhido"
                )}
              </span>
            </span>
            {comCliente && (
              <Link
                to="/equipe/contratos"
                className="shrink-0 font-medium underline-offset-2 hover:underline"
              >
                Trocar contrato
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Header moderno T4I */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo e título */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img
                  src="/T4I_logotipo_.png"
                  alt="T4I Solutions"
                  className="h-8 w-auto"
                />
                <div className="hidden h-6 w-px bg-gray-300 sm:block"></div>
                <h1 className="hidden whitespace-nowrap text-xl font-semibold bg-gradient-to-r from-blue-800 to-blue-600 bg-clip-text text-transparent sm:block">
                  Portal de Chamados
                </h1>
              </div>
            </div>

            {/* Navegação entre o Kanban e a gestão de chamados ("Quadro" virou "Kanban" em 05/10/2026) */}
            {comCliente && (
              <nav className="hidden md:flex items-center gap-1" aria-label="Seções">
                <LinksDasSecoes />
              </nav>
            )}

            {/* User info e logout */}
            <div className="flex items-center space-x-6">
              {user && (
                <>
                  {/* Desktop Version */}
                  <div className="hidden md:flex items-center space-x-3 px-4 py-2 bg-blue-50 rounded-lg">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-800">{user.nome}</p>
                        {user.administrador && (
                          <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
                            Admin
                          </span>
                        )}
                        {equipe && (
                          <span className="inline-block bg-violet-100 text-violet-800 px-2 py-0.5 rounded text-xs font-medium">
                            Equipe T4i
                          </span>
                        )}
                      </div>
                      {/* Da equipe, o cliente atendido já está na faixa. */}
                      {user.nomeCliente && !equipe && (
                        <p className="text-gray-600 text-xs">
                          {user.nomeCliente}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Mobile Version */}
                  <div className="flex md:hidden items-center space-x-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <div className="text-xs flex items-center space-x-1">
                      <p className="font-medium text-gray-800 truncate max-w-16">
                        {user.nome.split(" ")[0]}
                      </p>
                      {user.administrador && (
                        <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs font-medium">
                          A
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {capacidades?.escritasHabilitadas && !equipe && (
                <button
                  onClick={() => setTrocandoSenha(true)}
                  title="Trocar senha"
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                >
                  <KeyRound className="w-4 h-4" />
                  <span className="hidden lg:inline">Trocar senha</span>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </div>
        {/* No celular as seções ficam numa linha própria (06/10/2026): antes só
            existiam no computador, e no celular não havia como ir do Kanban à lista. */}
        {comCliente && (
          <nav className="flex justify-around border-t border-gray-100 px-2 py-1 md:hidden" aria-label="Seções">
            <LinksDasSecoes />
          </nav>
        )}
      </header>

      {/* Content com padding moderno */}
      <main className="max-w-7xl mx-auto py-4 px-4 sm:py-8 sm:px-6 lg:px-8">
        <div className="space-y-6">{children}</div>
      </main>

      {trocandoSenha && (
        <TrocarSenhaDialog aoFechar={() => setTrocandoSenha(false)} />
      )}

      {/* Footer sutil */}
      <footer className="mt-auto py-4 text-center text-xs text-gray-500">
        <div className="max-w-7xl mx-auto px-4">
          © 2026 T4i Solutions - Portal de Chamados
        </div>
      </footer>
    </div>
  );
};

const classeDoLink = ({ isActive }: { isActive: boolean }) =>
  [
    "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
    isActive ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-800",
  ].join(" ");

/** Kanban, Chamados e Descritivo: no cabeçalho do computador e na linha do celular. */
const LinksDasSecoes = () => (
  <>
    <NavLink to="/" end className={classeDoLink}>
      <KanbanSquare className="w-4 h-4" />
      Kanban
    </NavLink>
    <NavLink to="/chamados" className={classeDoLink}>
      <ListChecks className="w-4 h-4" />
      Chamados
    </NavLink>
    <NavLink to="/descritivos" className={classeDoLink}>
      <Clock className="w-4 h-4" />
      Descritivo
    </NavLink>
  </>
);

export default Layout;
